/**
 * ClarificationNote — Ando asking the user to pick a reading, inline in chat.
 *
 * When a data question is ambiguous ("l'importo" while the catalog holds BOTH
 * *Importo Lordo* and *Importo Netto*) the BE refuses to guess: a wrong number
 * delivered confidently is worse than a question. It returns N options; each
 * one carries the SAME question rewritten with the exact field name. Clicking
 * an option sends that rewrite as a new user turn — the second round answers
 * with the real figure.
 *
 * Lifecycle contrast with ``SourceConfirmationCard`` (whose visual language
 * this borrows): that one is a PENDING ACTION — it POSTs a confirmation to
 * release a blocked Temporal fork, so it needs a radio group + an explicit
 * Confirm button + a status machine. This one blocks nothing: the turn already
 * finished. So there is no selected state, no confirm step, no cancel — one
 * click IS the whole interaction, and every option is its own button.
 *
 * Pencil discipline (see the ando-ui-review skill):
 *   • built on ``HighlightNote tone="accent"`` — brand emphasis is a hairline
 *     CONTOUR, never a ``bg-ando-orange-soft`` wash;
 *   • the field name carries typographic weight, NOT a box — no chips, no
 *     pastel category badges (the V1 card's rainbow badges are the exact
 *     anti-pattern this replaces);
 *   • hover DARKENS ink and lights an orange hairline on the row's left edge;
 *     there is no hover fill anywhere;
 *   • small Lucide glyphs (14–15px), ``ink-1..ink-4`` ladder, compact density.
 *
 * Controlled-ish: the parent owns the send (``onSelect``); this component owns
 * only the "which row did I just click" latch, so a double-tap can't fire two
 * turns. Same ownership split as ``ConflictNote``'s open/closed state.
 *
 * Decoupled from ``@ando/core`` on purpose (``@ando/ui`` is a leaf package):
 * the props are structural, so the domain ``ClarificationNeededDisplay``
 * slots straight in.
 */
"use client";

import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  CalendarRange,
  Columns3,
  CornerDownLeft,
  Filter,
  HelpCircle,
  Layers,
  MessageCircleQuestion,
  Sigma,
  Table2,
  Tag,
} from "lucide-react";
import * as React from "react";

import { cn } from "../../lib/cn";
import { useI18n } from "../../lib/i18n";
import { HighlightNote } from "../pencil/HighlightNote";

/** Structural mirror of the domain ``ClarificationQuestion``. */
export interface ClarificationNoteOption {
  readonly question: string;
  readonly category?: string;
  readonly suggested_query?: string | null;
  readonly context?: {
    readonly term?: string;
    readonly chosen?: string;
    readonly candidates?: readonly string[];
  } | null;
}

export interface ClarificationNoteProps {
  readonly questions: readonly ClarificationNoteOption[];
  /** Persisted selection, recovered from the following retry turn. */
  readonly selectedQuery?: string;
  /** Raw connector error — shown only behind the quiet disclosure. */
  readonly error?: string | null;
  /** Fired with the option's ``suggested_query`` (guaranteed non-empty). */
  readonly onSelect: (suggestedQuery: string, option: ClarificationNoteOption) => void;
  readonly className?: string;
}

// ── Category → glyph + label ───────────────────────────────────────────────
// Icon-only differentiation (never colour-only): every category shares the
// same ink treatment, so the glyph + the label carry the meaning. An unknown
// category (the BE ships new ones — ``join`` landed Aug 10, 2026) degrades to
// the neutral glyph with NO label rather than leaking a raw snake_case token
// into the UI.

const CATEGORY_META: Record<
  string,
  {
    readonly icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
    readonly label: MessageDescriptor;
  }
> = {
  entity: { icon: Table2, label: msg`Entità` },
  column: { icon: Columns3, label: msg`Campo` },
  value: { icon: Tag, label: msg`Valore` },
  date_range: { icon: CalendarRange, label: msg`Periodo` },
  aggregation: { icon: Sigma, label: msg`Calcolo` },
  filter: { icon: Filter, label: msg`Filtro` },
  join: { icon: Layers, label: msg`Collegamento` },
  complexity: { icon: Layers, label: msg`Complessità` },
  general: { icon: HelpCircle, label: msg`Precisazione` },
};

function metaFor(category: string | undefined) {
  return CATEGORY_META[category ?? "general"];
}

/**
 * Split an option into (headline, detail).
 *
 * The BE hands over a full sentence ("Con «importo» intendi «Importo
 * Lordo»?"). When ``context.chosen`` is present we PROMOTE the exact field
 * name to the headline — that's the one word the user is actually choosing
 * between, and typographic weight beats re-reading the sentence. The original
 * sentence then becomes the detail line, so nothing the BE said is lost.
 *
 * With no ``chosen`` (complexity / date_range / generic failures) the sentence
 * IS the headline and there is no detail — a plain readable line.
 */
function splitOption(option: ClarificationNoteOption): {
  readonly headline: string;
  readonly detail: string | null;
} {
  const chosen = option.context?.chosen?.trim();
  const question = option.question.trim();
  if (chosen && chosen.length > 0) return { headline: chosen, detail: question };
  return { headline: question, detail: null };
}

export const ClarificationNote: React.FC<ClarificationNoteProps> = ({
  questions,
  error,
  onSelect,
  selectedQuery,
  className,
}) => {
  const { i18n } = useI18n();
  const [localSentIndex, setSentIndex] = React.useState<number | null>(null);
  const persistedIndex = selectedQuery
    ? questions.findIndex((option) => option.suggested_query?.trim() === selectedQuery.trim())
    : -1;
  const sentIndex = persistedIndex >= 0 ? persistedIndex : localSentIndex;
  const [showError, setShowError] = React.useState(false);
  const errorPanelId = React.useId();

  const handleSelect = React.useCallback(
    (index: number, option: ClarificationNoteOption) => {
      // Latch: a clarification round-trips through the agent, so a second
      // click within the same render would queue a duplicate turn.
      if (sentIndex !== null) return;
      const query = option.suggested_query?.trim();
      if (!query) return;
      setSentIndex(index);
      onSelect(query, option);
    },
    [onSelect, sentIndex],
  );

  // Empty list → render nothing. The agent's prose above already explains it
  // needs more detail; an empty framed box would be pure noise.
  if (questions.length === 0) return null;

  // The ambiguous term is the same across every option of a metric ambiguity —
  // quoting it ONCE in the sub-line beats repeating it on each row.
  const term = questions.find((q) => q.context?.term?.trim())?.context?.term?.trim();

  return (
    <HighlightNote
      tone="accent"
      icon={<MessageCircleQuestion size={12} strokeWidth={1.7} aria-hidden />}
      label={<Trans>Serve una precisazione</Trans>}
      className={cn("w-full motion-safe:animate-msg-in", className)}
    >
      <p className="text-[12.5px] text-ink-3 leading-snug">
        {term ? (
          <Trans>
            Nei tuoi dati «{term}» può voler dire più di una cosa. Scegli quale intendi e rifaccio
            il calcolo.
          </Trans>
        ) : (
          <Trans>
            Non ho abbastanza dettagli per rispondere con certezza. Scegli un'opzione e riprovo.
          </Trans>
        )}
      </p>

      <ul className="mt-1 flex flex-col">
        {questions.map((option, index) => {
          const { headline, detail } = splitOption(option);
          const meta = metaFor(option.category);
          const Icon = meta?.icon ?? HelpCircle;
          const categoryLabel = meta ? i18n._(meta.label) : null;
          const actionable = Boolean(option.suggested_query?.trim());
          const isSent = sentIndex === index;
          const isDimmed = sentIndex !== null && !isSent;
          const key = `${option.category ?? "general"}-${headline}-${index}`;

          // Non-actionable entries (BE sent a question with no rewrite) are
          // prose, not affordances — rendering them as dead buttons would be
          // a keyboard trap with nothing behind it.
          if (!actionable) {
            return (
              <li
                key={key}
                className="flex items-baseline gap-2.5 border-hairline border-t py-2.5 first:border-t-0"
              >
                <span className="relative top-0.5 shrink-0 text-ink-4">
                  <Icon size={14} strokeWidth={1.7} aria-hidden />
                </span>
                <span className="min-w-0 text-[13px] text-ink-2 leading-snug">{headline}</span>
              </li>
            );
          }

          return (
            <li key={key} className="border-hairline border-t first:border-t-0">
              <button
                type="button"
                disabled={sentIndex !== null}
                onClick={() => handleSelect(index, option)}
                className={cn(
                  // Left rule stays in the layout at all times (transparent at
                  // rest) so lighting it on hover never shifts the text.
                  "group/opt flex w-full items-baseline gap-2.5 border-transparent border-l-2 py-2.5 pr-1 pl-2 text-left",
                  "transition-colors",
                  "focus-visible:outline-2 focus-visible:outline-ando-orange focus-visible:outline-offset-2",
                  sentIndex === null && "hover:border-ando-orange/45",
                  isDimmed && "opacity-45",
                  sentIndex !== null && "cursor-default",
                )}
              >
                <span
                  className={cn(
                    "relative top-0.5 shrink-0 text-ink-4 transition-colors",
                    sentIndex === null && "group-hover/opt:text-ando-orange",
                  )}
                >
                  <Icon size={14} strokeWidth={1.7} aria-hidden />
                </span>

                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span
                    className={cn(
                      "font-semibold text-[14px] text-ink-1 leading-snug tracking-tight transition-colors",
                      isDimmed && "font-medium text-ink-2",
                    )}
                  >
                    {headline}
                  </span>
                  {detail ? (
                    <span className="text-[11.5px] text-ink-4 leading-snug">
                      {categoryLabel ? `${categoryLabel} · ` : null}
                      {detail}
                    </span>
                  ) : null}
                </span>

                <span
                  className={cn(
                    "relative top-0.5 ml-auto inline-flex shrink-0 items-center gap-1 text-[11px] text-ink-4 transition-colors",
                    sentIndex === null && "group-hover/opt:text-ando-orange",
                  )}
                >
                  {isSent ? (
                    <Trans>selezionata</Trans>
                  ) : (
                    <CornerDownLeft size={14} strokeWidth={1.7} aria-hidden />
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {/* Screen-reader confirmation: the visual "inviata" tag sits inside a
          row that has just been disabled, so it wouldn't be announced. */}
      <p aria-live="polite" className="sr-only">
        {sentIndex !== null ? <Trans>Scelta registrata. Ando riprende l'analisi.</Trans> : null}
      </p>

      {error ? (
        <>
          <button
            type="button"
            aria-expanded={showError}
            aria-controls={errorPanelId}
            onClick={() => setShowError((v) => !v)}
            className={cn(
              "mt-1.5 rounded-[3px] py-1 text-[11px] text-ink-4 transition-colors hover:text-ink-2",
              "focus-visible:outline-2 focus-visible:outline-ando-orange focus-visible:outline-offset-2",
            )}
          >
            {showError ? <Trans>Nascondi dettagli tecnici</Trans> : <Trans>Dettagli tecnici</Trans>}
          </button>
          {showError ? (
            <p
              id={errorPanelId}
              className="mt-1 break-words border-hairline border-t pt-1.5 font-mono text-[10.5px] text-ink-4 leading-snug"
            >
              {error}
            </p>
          ) : null}
        </>
      ) : null}
    </HighlightNote>
  );
};

ClarificationNote.displayName = "ClarificationNote";
